import { type AppointmentCollectionStep } from '@/models/appointment-collection-step.ts';
import { AppointmentDisplayState } from '@/models/appointment-display-state.ts';
import { type Appointment, AppointmentBookStatus } from '@/models/appointment.ts';
import { type Practitioner } from '@/models/practitioner.ts';
import { type PrismicLocalizedDoctor, type PrismicLocalizedLocation } from '@/models/prismic-localized.ts';

import { PVSProvider } from '@/utils/availabilityUtils.js';
import { dayjs } from '@/utils/dayjsSetup.js';

const ONLINE_JOIN_WINDOW_MINUTES = 30;
const ALLOWED_URL_SCHEMES = ['https:'];

export const CHAIN_STATUS_LABEL_KEYS: Record<AppointmentBookStatus, string> = {
    [AppointmentBookStatus.BOOKED]: 'ChainStatusUpcoming',
    [AppointmentBookStatus.PENDING_BOOKING]: 'ChainStatusUpcoming',
    [AppointmentBookStatus.COMPLETED]: 'ChainStatusPast',
    [AppointmentBookStatus.CANCELLED]: 'ChainStatusCancelled',
    [AppointmentBookStatus.DECLINED]: 'ChainStatusCancelled',
};

export const getChainName = (appointment: Appointment, language: string): string => {
    const key = language.startsWith('de') ? 'de' : 'en';
    const chainName = appointment.collection_data?.chain_name;
    return chainName?.[key] ?? chainName?.en ?? '';
};

// Backend names look like "Last, First, DD.MM.YYYY, ID:<n>, <appointment type>". Strip everything up to
// the "ID:<n>," marker so only the appointment type remains, keeping any commas it may itself contain.
export const titleAfterId = (label: string): string => {
    const match = /ID:[^,]*,\s*(.*)/.exec(label);
    return match ? match[1].trim() : label;
};

export const isSafeUrl = (raw: string | undefined): raw is string => {
    if (!raw) return false;
    try {
        return ALLOWED_URL_SCHEMES.includes(new URL(raw).protocol);
    } catch {
        return false;
    }
};

export const getLocalizedDoctor = (
    appointment: Appointment | undefined,
    lan: string
): PrismicLocalizedDoctor | undefined => appointment?.doctor?.[lan];

export const getLocalizedLocation = (
    appointment: Appointment | undefined,
    lan: string
): PrismicLocalizedLocation | undefined => appointment?.location?.[lan];

export const buildClickablePractitioner = (
    doctor: PrismicLocalizedDoctor | undefined,
    onNavigate: (key: string) => void
): (Practitioner & { onClick: () => void }) | undefined => {
    const key = doctor?.key?.[0]?.text;
    const url = doctor?.profile_picture?.url;
    const name = doctor?.display_name?.[0]?.text ?? doctor?.practice_name?.[0]?.text;
    if (!key || !isSafeUrl(url) || !name) return undefined;
    return {
        key: key,
        url: url,
        name: name,
        onClick: () => onNavigate(key),
    };
};

export const formatPractitionerSubtitle = (doctor: PrismicLocalizedDoctor | undefined): string | undefined => {
    const name = doctor?.display_name?.[0]?.text;
    const practice = doctor?.practice_name?.[0]?.text;
    const parts = [name, practice].filter((part): part is string => Boolean(part));
    if (parts.length === 0) return undefined;
    return parts.join(' | ');
};

export const formatLongAppointmentDate = (start: Date | string | undefined): string => {
    if (!start) return '';
    return dayjs(start).format('dddd D. MMMM YYYY, HH:mm');
};

export const isAppointmentCancelledExternally = (appointment: Appointment | undefined): boolean =>
    appointment?.status === AppointmentBookStatus.CANCELLED && appointment?.is_cancellation_confirmed === false;

export const isOnlineJoinDisabled = (appointment: Appointment | undefined, minutesUntilStart: number): boolean =>
    appointment?.pvs_provider === PVSProvider.DATA_CUR ||
    !isSafeUrl(appointment?.links?.online_url) ||
    minutesUntilStart > ONLINE_JOIN_WINDOW_MINUTES;

export const getPrismicText = (dataSet: boolean, field: { text: string }[] | undefined, fallback: string): string =>
    dataSet ? (field?.[0]?.text ?? fallback) : fallback;

export const sortStepsByDate = (steps: AppointmentCollectionStep[]): AppointmentCollectionStep[] =>
    [...steps].sort((a, b) => a.date.getTime() - b.date.getTime());

export const mapStatusToDisplayState = (status: AppointmentBookStatus): AppointmentDisplayState => {
    if (status === AppointmentBookStatus.COMPLETED) return AppointmentDisplayState.PAST;
    if (status === AppointmentBookStatus.CANCELLED || status === AppointmentBookStatus.DECLINED) {
        return AppointmentDisplayState.CANCELLED;
    }
    return AppointmentDisplayState.UPCOMING;
};

export const formatShortDate = (date: Date | string): string => dayjs(date).format('DD.MM.YYYY');

export const getCollectionInstanceId = (appointment: Appointment | undefined): string | undefined =>
    appointment?.data_cur_links?.instance_id ?? appointment?.links?.dc_instance_id;

export const isChainCancellable = (steps: AppointmentCollectionStep[]): boolean =>
    steps.some((step) => step.status === AppointmentBookStatus.BOOKED && dayjs(step.date).isAfter(dayjs()));

export const collectChainPractitioners = (
    sortedSteps: AppointmentCollectionStep[],
    mainAppointment: Appointment,
    lan: string,
    onNavigate: (key: string) => void
): (Practitioner & { onClick: () => void })[] => {
    const seen = new Set<string>();
    const list: (Practitioner & { onClick: () => void })[] = [];
    const add = (doctor: PrismicLocalizedDoctor | undefined): void => {
        const p = buildClickablePractitioner(doctor, onNavigate);
        if (!p || seen.has(p.key)) return;
        seen.add(p.key);
        list.push(p);
    };
    add(mainAppointment.doctor?.[lan]);
    sortedSteps.forEach((step) => add(step.appointmentData?.doctor?.[lan]));
    return list;
};
